import { useLocation, useParams } from 'react-router-dom';
import { useWeapons } from './useWeapons';
import { useI18n } from '../i18n';

export interface Scope {
  weaponCode: string | null;
  category: string | null;
  kind: string | null;
  /** Prefixo das rotas filhas: /w/<arma>, /c/<categoria> ou /all. */
  basePath: string;
  /** Como chamar a API: { type }, { category } ou nada (tudo do catálogo). */
  apiScope: { type?: string; category?: string; kind?: string };
  label: string;
  isCategory: boolean;
  isAll: boolean;
}

/** Uma tela do Nível 2+ pode estar filtrada por arma, por categoria ou por nada. */
export function useScope(): Scope {
  const { weaponCode, category, kind } = useParams();
  const { pathname } = useLocation();
  const cat = category ? decodeURIComponent(category) : null;

  if (kind) {
    return {
      weaponCode: null,
      category: null,
      kind,
      basePath: `/k/${kind}`,
      apiScope: { kind },
      label: kind,
      isCategory: false,
      isAll: false,
    };
  }

  if (cat) {
    return {
      weaponCode: null,
      category: cat,
      kind: null,
      basePath: `/c/${encodeURIComponent(cat)}`,
      apiScope: { category: cat },
      label: cat,
      isCategory: true,
      isAll: false,
    };
  }

  if (weaponCode) {
    return {
      weaponCode,
      category: null,
      kind: null,
      basePath: `/w/${weaponCode}`,
      apiScope: { type: weaponCode },
      label: weaponCode,
      isCategory: false,
      isAll: false,
    };
  }

  // /all — tudo que já está no catálogo, sem varrer o Youpin.
  return {
    weaponCode: null,
    category: null,
    kind: null,
    basePath: pathname.startsWith('/all') ? '/all' : '/all',
    apiScope: { category: 'all' },
    label: 'all',
    isCategory: false,
    isAll: true,
  };
}

/**
 * Rótulo legível do escopo atual: nome da arma, nome traduzido do tipo, ou
 * "Todas". Usado nos breadcrumbs das telas de nível 2, 2.5 e 3.
 */
export function useScopeLabel(scope: Scope): string {
  const { weaponName, kindName } = useWeapons();
  const { t } = useI18n();
  if (scope.isAll) return t('filters.allFem');
  if (scope.kind) return kindName(scope.kind);
  if (scope.isCategory) return scope.label;
  return weaponName(scope.weaponCode) || scope.label;
}
